import type { AnalyticsInstance } from 'analytics';
import { EXCLUDE_GTM, gtmOptionsFor } from './gtmEvents';
import type { EventName, EventPayload } from './types';

/**
 * Typed facade over an `analytics` (getanalytics.io) instance.
 *
 * The core handles queueing, identity persistence, and fan-out to destination
 * plugins. This facade adds what the core doesn't: compile-time `EventMap`
 * safety on `track`, call-time enrichment (`$insert_id`, `firedAt`, super
 * properties), and per-event GTM gating via `options.plugins`. Operations
 * never throw — a broken instance degrades silently.
 */
export interface AnalyticsClient {
  /** Fire an event. Type-safe. Never throws. */
  track: <E extends EventName>(name: E, properties: EventPayload<E>) => void;
  identify: (distinctId: string) => void;
  reset: () => void;
  /** Merge super properties stamped onto every subsequent event. */
  register: (props: Record<string, unknown>) => void;
  peopleSet: (props: Record<string, unknown>) => void;
  /** Resolves once all plugins are ready (or skipped). Tests await it. */
  ready: Promise<unknown>;
}

function genInsertId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `e_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function safeWarn(message: string, err: unknown): void {
  try {
    console.error(`[analytics] ${message}`, err);
  } catch {
    // logging itself must never crash the host
  }
}

/**
 * Wrap an `analytics` instance in the typed client. Super properties live in
 * closure and are merged at call time (the core has no super-property
 * primitive). `identify`/`peopleSet` always exclude the GTM plugin so identity
 * traits (PII) never reach the dataLayer.
 */
export function createAnalyticsClient(instance: AnalyticsInstance): AnalyticsClient {
  let superProps: Record<string, unknown> = {};
  let lastDistinctId: string | null = null;

  const ready: Promise<unknown> = new Promise((resolve) => {
    try {
      instance.ready(() => resolve(instance));
    } catch (err) {
      safeWarn('ready failed', err);
      resolve(null);
    }
  });

  return {
    ready,
    track: <E extends EventName>(name: E, properties: EventPayload<E>) => {
      try {
        const props = properties as Record<string, unknown>;
        // Capture $insert_id + firedAt at CALL time (not flush time) so a
        // queued event keeps the timestamp the caller saw.
        const enriched = {
          ...superProps,
          ...props,
          $insert_id: genInsertId(),
          firedAt: typeof props.firedAt === 'number' ? props.firedAt : Date.now(),
        };
        void instance.track(name, enriched, gtmOptionsFor(name)).catch((err) => safeWarn(`track ${name}`, err));
      } catch (err) {
        safeWarn(`track ${name}`, err);
      }
    },
    identify: (distinctId) => {
      try {
        lastDistinctId = distinctId;
        void instance.identify(distinctId, {}, EXCLUDE_GTM).catch((err) => safeWarn('identify', err));
      } catch (err) {
        safeWarn('identify', err);
      }
    },
    reset: () => {
      try {
        lastDistinctId = null;
        void instance.reset().catch((err) => safeWarn('reset', err));
      } catch (err) {
        safeWarn('reset', err);
      }
    },
    register: (props) => {
      superProps = { ...superProps, ...props };
    },
    peopleSet: (props) => {
      try {
        // identify(id, traits) updates traits on the bound user; the Mixpanel
        // plugin forwards them as `people.set`. Needs an id — reconcileIdentity
        // always identifies before peopleSet, but fall back to the core's
        // persisted user id just in case.
        const id = lastDistinctId ?? (instance.user('userId') as string | undefined);
        if (!id) return;
        void instance.identify(id, props, EXCLUDE_GTM).catch((err) => safeWarn('peopleSet', err));
      } catch (err) {
        safeWarn('peopleSet', err);
      }
    },
  };
}

/** No-op client. Used as the default singleton before the provider mounts. */
export function createNoopClient(): AnalyticsClient {
  return {
    ready: Promise.resolve(null),
    track: () => undefined,
    identify: () => undefined,
    reset: () => undefined,
    register: () => undefined,
    peopleSet: () => undefined,
  };
}

/**
 * Module-level handle to the active client. The React context exposes the same
 * instance to component callsites; this accessor exists for non-React code
 * paths — primarily the MutationCache callbacks, which run outside the tree.
 */
let currentClient: AnalyticsClient = createNoopClient();

export function setAnalyticsClient(client: AnalyticsClient): void {
  currentClient = client;
}

export function getAnalyticsClient(): AnalyticsClient {
  return currentClient;
}

export type { EventMap, EventName, EventPayload } from './types';
